import {
  InputField,
  RadioButton,
  SearchableSelect,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { MFA_METHOD_OPTIONS } from "../constants";
import { MfaMethod } from "../types";
import { SecretKeyField } from "./SecretKeyField";
import { YesNoRadio } from "./YesNoRadio";

interface MfaConfigFieldsetProps {
  mfaPresent: string;
  mfaPresentError: string | undefined;
  onMfaPresentChange: (value: string) => void;
  mfaMethod: MfaMethod;
  onMfaMethodChange: (value: string) => void;
  isEditMode: boolean;
  isSecretKeyEditing: boolean;
  onSecretKeyEditClick: () => void;
  onSecretKeyChange: (value: string) => void;
  onSecretKeyBlur: () => void;
  secretKeyError: string | undefined;
  email: string;
  emailError: string | undefined;
  onEmailChange: (value: string) => void;
  modalEmailOptions: string[];
  emailSelectKey: string;
  maxConcurrency: string;
  maxConcurrencyError: string | undefined;
  onMaxConcurrencyChange: (value: string) => void;
  onMaxConcurrencyBlur: () => void;
}

/**
 * The "MFA Present" Yes/No radio plus the nested configuration fieldset
 * that appears when MFA is on. The nested fieldset itself flips between
 * a Secret Key input and an OTP-inbox email picker based on `mfaMethod`.
 * When MFA is off, a "Max Concurrency" number field is shown instead,
 * since concurrency only applies to non-queued (non-MFA) credentials.
 * It is also shown before MFA has been answered (add mode, where
 * `mfaPresent` starts blank) so the field isn't edit-mode only.
 *
 * `emailSelectKey` is a caller-provided key (composed from the credential
 * id and current email) that forces `SearchableSelect` to remount when
 * the chosen email or the underlying options list changes. The remount
 * is needed because the select otherwise caches its initial
 * `defaultValue` and ignores subsequent updates from form state.
 */
export const MfaConfigFieldset = ({
  mfaPresent,
  mfaPresentError,
  onMfaPresentChange,
  mfaMethod,
  onMfaMethodChange,
  isEditMode,
  isSecretKeyEditing,
  onSecretKeyEditClick,
  onSecretKeyChange,
  onSecretKeyBlur,
  secretKeyError,
  email,
  emailError,
  onEmailChange,
  modalEmailOptions,
  emailSelectKey,
  maxConcurrency,
  maxConcurrencyError,
  onMaxConcurrencyChange,
  onMaxConcurrencyBlur,
}: MfaConfigFieldsetProps) => (
  <>
    <YesNoRadio
      label="MFA Present"
      name="mfa_present"
      value={mfaPresent}
      error={mfaPresentError}
      onChange={onMfaPresentChange}
    />
    {mfaPresent === "true" && (
      <fieldset className="mt-2 flex flex-col gap-3 rounded-md border border-solid border-primaryGray-14 bg-white p-3">
        <legend className="rounded bg-primaryGray-16 p-1 text-xs font-semibold">
          Multi Factor Authentication Configuration
        </legend>
        <RadioButton
          name="mfa_method"
          direction="horizontal"
          value={mfaMethod}
          onChange={onMfaMethodChange}
          options={MFA_METHOD_OPTIONS}
        />
        {mfaMethod === "secret_key" ? (
          <SecretKeyField
            error={secretKeyError}
            isEditMode={isEditMode}
            isEditing={isSecretKeyEditing}
            onEditClick={onSecretKeyEditClick}
            onChange={onSecretKeyChange}
            onBlur={onSecretKeyBlur}
          />
        ) : (
          <SearchableSelect
            key={emailSelectKey}
            id="email"
            label="Email"
            required
            placeholder="Select Email"
            searchPlaceholder="Search emails"
            defaultValue={email}
            value={email}
            error={emailError}
            options={modalEmailOptions.map((e) => ({
              label: e,
              value: e,
            }))}
            onOptionChange={(option) => onEmailChange(option.value)}
          />
        )}
      </fieldset>
    )}
    {mfaPresent !== "true" && (
      <InputField
        label="Max Concurrency"
        type="number"
        size="md"
        fullWidth
        required
        min={1}
        hint="Maximum number of concurrent sessions allowed for this credential."
        error={maxConcurrencyError}
        defaultValue={maxConcurrency}
        id="max_concurrent"
        onChange={(e) => onMaxConcurrencyChange(e.target.value)}
        onBlur={onMaxConcurrencyBlur}
      />
    )}
  </>
);
